/* eslint-disable react/no-this-in-sfc */
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import { getExcludingDate, getPayDate } from "components/company/v3/contents/events/dividends/DividendsPaid";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type { SeriesOptionsType } from "highcharts";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import parseDateYmd from "lib/utils/parseDateYmd";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface DividendsPaidChartProps {
  dividendsPaid: IDividendsPaidDetails[];
  currency: "BRL" | "USD";
}

export default function DividendsPaidChart({ dividendsPaid, currency }: DividendsPaidChartProps) {
  const intl = useIntl();
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const appTheme = useTheme();
  const slateOrDarkSlate = appTheme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = appTheme === "dark" ? colors.slate[300] : colors.slate[500];
  const legendItemHoverColor = appTheme === "dark" ? colors.slate[400] : colors.slate[500];
  const labels = {
    type: intl.formatMessage({ defaultMessage: "Tipo", id: "wt+XcA" }),
    recordDate: intl.formatMessage({ defaultMessage: "Data-Com", id: "uJFyej" }),
    excludingDate: intl.formatMessage({ defaultMessage: "Data-Ex", id: "tsg1zP" }),
    payDate: intl.formatMessage({ defaultMessage: "Pagamento", id: "23+rmI" }),
    amountPaid:
      currency === "BRL"
        ? intl.formatMessage({
            defaultMessage: "Valor (R$)",
            id: "XydT1i",
            description: "Valor dos dividendos pagos em reais",
          })
        : intl.formatMessage({
            defaultMessage: "Valor (U$)",
            id: "gHZX1a",
            description: "Valor dos dividendos pagos em dólar",
          }),
  };

  const formatAmount = (amount: number) => {
    if (currency === "BRL") {
      return numeral(amount).format("0.00").replace(".", ",");
    }
    return trimEnd(trimEnd(numeral(amount).format("0.000").replace(".", ","), "0"), ",");
  };

  // Brazilian market splits by dividend type (JUROS vs others), American doesn't
  const series: SeriesOptionsType[] =
    currency === "BRL"
      ? [
          dividendsPaid.filter((node) => node.dividendType !== "JUROS"),
          dividendsPaid.filter((node) => node.dividendType === "JUROS"),
        ].map((dividends, index) => {
          const theme = getColorThemeForIndex(index, appTheme);

          if (!theme) {
            throw new Error("Theme not found.");
          }

          const name = index === 0 ? intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" }) : "JCP";

          return {
            type: "column",
            showInNavigator: true,
            animation: false,
            name,
            color: theme.color,
            borderColor: theme.lineColor,
            data: dividends
              .sort((a, b) => compareAsc(parseDateYmd(a.recordDate), parseDateYmd(b.recordDate)))
              .map((node) => {
                const type =
                  node.dividendType === "JUROS"
                    ? "JCP"
                    : intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" });

                return {
                  x: parseISO(node.recordDate).getTime(),
                  y: node.amountPaid,
                  name: type,
                  custom: node,
                };
              }),
            custom: {
              primaryColor: theme.color,
              secondaryColor: theme.lineColor,
            },
          };
        })
      : (() => {
          const theme = getColorThemeForIndex(0, appTheme);

          if (!theme) {
            throw new Error("Theme not found.");
          }

          return [
            {
              type: "column" as const,
              showInNavigator: true,
              animation: false,
              color: theme.color,
              borderColor: theme.lineColor,
              data: [...dividendsPaid]
                .sort((a, b) => compareAsc(parseDateYmd(a.recordDate), parseDateYmd(b.recordDate)))
                .map((node) => ({
                  x: parseISO(node.recordDate).getTime(),
                  y: node.amountPaid,
                  custom: node,
                })),
              custom: {
                primaryColor: theme.color,
                secondaryColor: theme.lineColor,
              },
            },
          ];
        })();

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      tickColor: slateOrDarkSlate,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          return intl.formatDate(new Date(this.value), {
            year: "2-digit",
            month: "numeric",
            day: "numeric",
          });
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: appTheme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    yAxis: {
      type: "linear",
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: appTheme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    series,
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: dividendsPaid.length > 10,
      ...(appTheme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...(appTheme === "dark" ? darkNavigatorStyles : navigatorStyles).yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    legend: {
      align: "left",
      enabled: currency === "BRL",
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
      itemHoverStyle: {
        color: legendItemHoverColor,
      },
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: appTheme === "dark" ? colors.slate[800] : colors.white,
      borderColor: appTheme === "dark" ? colors.slate[950] : colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: false,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const point = (this.point as any).custom as IDividendsPaidDetails | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        const keyValues =
          currency === "BRL"
            ? [
                {
                  key: labels.type,
                  value:
                    point.dividendType === "JUROS"
                      ? "JCP"
                      : intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" }),
                },
                {
                  key: labels.excludingDate,
                  value:
                    point.excludingDate && point.recordDate
                      ? getExcludingDate(intl, parseISO(point.recordDate), parseISO(point.excludingDate))
                      : "-",
                },
                {
                  key: labels.recordDate,
                  value: intl.formatDate(parseISO(point.recordDate), {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                  }),
                },
                {
                  key: labels.payDate,
                  value: getPayDate(parseISO(point.payDate), intl),
                },
                { key: labels.amountPaid, value: formatAmount(point.amountPaid) },
              ]
            : [
                {
                  key: labels.excludingDate,
                  value:
                    point.excludingDate && point.recordDate
                      ? getExcludingDate(intl, parseISO(point.recordDate), parseISO(point.excludingDate))
                      : "-",
                },
                {
                  key: labels.recordDate,
                  value: formatDateOrReturnHyphen(intl, point.recordDate),
                },
                {
                  key: labels.payDate,
                  value: formatDateOrReturnHyphen(intl, point.payDate),
                },
                {
                  key: labels.amountPaid,
                  value: formatAmount(point.amountPaid),
                },
              ];

        return renderToStaticMarkup(
          <KeyValueTooltip
            keyValues={keyValues}
            x={intl.formatDate(parseISO(point.recordDate), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: appTheme === "dark" ? colors.slate[400] : colors.slate[500],
            color: appTheme === "dark" ? colors.slate[400] : colors.slate[50],
            lineWidth: 2,
          },
        },
        events:
          currency === "BRL"
            ? {
                legendItemClick(event) {
                  handleClickInLegend(event, this, chartRef);
                },
              }
            : {},
      },
      column: {
        stacking: "normal",
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />;
}
